import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { BusinessMap } from "@/components/business/BusinessMap";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import Seo from "@/components/seo/Seo";
import { SignInRequired } from "@/components/SignInRequired";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { useCommunityTalkCapabilities } from "@/hooks/use-community-talk-capabilities";
import { useToast } from "@/hooks/use-toast";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { communityTalkCategories, type CommunityTalk } from "@shared/schema";
import { buildTopicCommunityContent } from "@shared/topic-community-content";
import {
  isTopicContentIntent,
  parseTopicPostMetadataFromSearch,
  resolveTopicPostMetadata,
  type PersistedTopicPostMetadata,
  type TopicContentIntent,
} from "@shared/topic-post-metadata";
import {
  getTopicDescription,
  getTopicLabel,
  getTopicStageLabel,
  getTopicStages,
  OZK_TOPICS,
} from "@shared/topic-taxonomy";
import { inferZoneIdFromLocation } from "@shared/zones";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useRoute } from "wouter";
import { z } from "zod";

const CREATE_POST_DRAFT_KEY = "ozk_local_create_post_draft_v1";

const formSchema = z.object({
  title: z.string().min(1, "제목을 입력하세요"),
  content: z.string().min(10, "내용을 10자 이상 입력하세요"),
  category: z.string(),
  region: z.string(),
  suburb: z.string(),
  imageUrls: z.array(z.string()).optional(),
  restaurantAddress: z.string().optional(),
  restaurantLat: z.number().optional(),
  restaurantLng: z.number().optional(),
  topicId: z.string().optional(),
  topicStageId: z.string().optional(),
  contentIntent: z.enum(["question", "experience"]).optional(),
});

type FormData = z.infer<typeof formSchema>;

type CommunityTalkEditDTO = CommunityTalk & {
  metadata?: PersistedTopicPostMetadata | null;
  location?: Record<string, unknown> | string | null;
};

type CommunityTalkFormProps = {
  intent?: TopicContentIntent;
};

export default function CommunityTalkForm({ intent }: CommunityTalkFormProps) {
  const [match, params] = useRoute<{ id: string }>("/community-talk/edit/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { toast } = useToast();
  const { locale, isKorean } = useTopicLocale();
  const { capabilities } = useCommunityTalkCapabilities();
  const metadataPersistenceEnabled = capabilities.topicMetadataPersistence;
  const initialTopicMetadata = useMemo(
    () =>
      typeof window === "undefined"
        ? resolveTopicPostMetadata({})
        : parseTopicPostMetadataFromSearch(window.location.search),
    [],
  );

  const id = match && params ? params.id : undefined;
  const isEditMode = Boolean(id);
  const requestedIntent = intent ?? initialTopicMetadata.metadata.contentIntent;

  const { data: existingPost } = useQuery<CommunityTalkEditDTO>({
    queryKey: [`/api/community-talk/${id}`],
    enabled: isEditMode,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      content: "",
      category: "general",
      region: selectedRegion !== "ALL" ? selectedRegion : "NSW",
      suburb: selectedSuburb || "Sydney",
      imageUrls: [],
      restaurantAddress: "",
      restaurantLat: undefined,
      restaurantLng: undefined,
      topicId: initialTopicMetadata.metadata.topicId ?? "",
      topicStageId: initialTopicMetadata.metadata.topicStageId ?? "",
      contentIntent: requestedIntent,
    },
  });

  const selectedCategory = form.watch("category");
  const isRestaurantCategory = selectedCategory === "restaurant";
  const selectedTopicId = form.watch("topicId") || "";
  const selectedTopicStageId = form.watch("topicStageId") || "";
  const selectedIntent = form.watch("contentIntent");
  const effectiveIntent = intent ?? selectedIntent;
  const isQuestion = effectiveIntent === "question";
  const isExperience = effectiveIntent === "experience";
  const selectedTopic = selectedTopicId
    ? OZK_TOPICS.find((topic) => topic.id === selectedTopicId)
    : undefined;
  const topicStages = selectedTopicId ? getTopicStages(selectedTopicId) : [];
  const selectedStage = topicStages.find(
    (stage) => stage.id === selectedTopicStageId,
  );
  const topicCommunityContent = selectedTopic
    ? buildTopicCommunityContent(selectedTopic)
    : undefined;
  const stageCommunityContent =
    selectedStage && topicCommunityContent
      ? topicCommunityContent.stages[selectedStage.id]
      : undefined;

  useEffect(() => {
    if (!selectedTopicStageId) return;
    const hasCompatibleStage = topicStages.some(
      (stage) => stage.id === selectedTopicStageId,
    );
    if (!hasCompatibleStage) {
      form.setValue("topicStageId", "");
    }
  }, [form, selectedTopicStageId, topicStages]);

  useEffect(() => {
    if (isEditMode) return;

    try {
      const rawDraft = sessionStorage.getItem(CREATE_POST_DRAFT_KEY);
      if (!rawDraft) return;

      const draft = JSON.parse(rawDraft);
      if (draft?.type !== "talk") return;

      const draftTopicMetadata = resolveTopicPostMetadata({
        topicId: typeof draft.topicId === "string" ? draft.topicId : undefined,
        topicStageId:
          typeof draft.topicStageId === "string"
            ? draft.topicStageId
            : undefined,
        contentIntent:
          typeof draft.contentIntent === "string"
            ? draft.contentIntent
            : undefined,
      }).metadata;

      form.reset({
        title: typeof draft.title === "string" ? draft.title : "",
        content:
          typeof draft.description === "string" ? draft.description : "",
        category:
          typeof draft.category === "string" ? draft.category : "general",
        region: typeof draft.state === "string" ? draft.state : "NSW",
        suburb: typeof draft.suburb === "string" ? draft.suburb : "Sydney",
        imageUrls: [],
        restaurantAddress: "",
        restaurantLat: undefined,
        restaurantLng: undefined,
        topicId:
          draftTopicMetadata.topicId ??
          initialTopicMetadata.metadata.topicId ??
          "",
        topicStageId:
          draftTopicMetadata.topicStageId ??
          initialTopicMetadata.metadata.topicStageId ??
          "",
        contentIntent:
          intent ??
          draftTopicMetadata.contentIntent ??
          initialTopicMetadata.metadata.contentIntent,
      });
      sessionStorage.removeItem(CREATE_POST_DRAFT_KEY);
    } catch (error) {
      console.warn("[CommunityTalkForm] Failed to load create draft", error);
    }
  }, [
    form,
    initialTopicMetadata.metadata.contentIntent,
    initialTopicMetadata.metadata.topicId,
    initialTopicMetadata.metadata.topicStageId,
    intent,
    isEditMode,
  ]);

  useEffect(() => {
    if (!existingPost) return;

    const location = parseLocation(existingPost.location);
    const metadata = existingPost.metadata ?? null;

    form.reset({
      title: existingPost.title ?? "",
      content: existingPost.content ?? "",
      category: existingPost.category ?? "general",
      region:
        existingPost.region ??
        readString(location.state) ??
        readString(location.region) ??
        "NSW",
      suburb:
        existingPost.suburb ?? readString(location.suburb) ?? "Sydney",
      imageUrls: existingPost.imageUrls || [],
      restaurantAddress: readString(location.address) ?? "",
      restaurantLat: readNumber(location.lat),
      restaurantLng: readNumber(location.lng),
      topicId: metadata?.topicId ?? "",
      topicStageId: metadata?.topicStageId ?? "",
      contentIntent:
        intent ??
        metadata?.contentIntent ??
        (isTopicContentIntent(readString(location.contentIntent))
          ? (readString(location.contentIntent) as TopicContentIntent)
          : undefined),
    });
  }, [existingPost, form, intent]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/community-talk", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community-talk"] });
      toast({ description: "게시글이 작성되었습니다" });
      navigate("/community-talk");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        description: error.message || "게시글 작성에 실패했습니다",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/community-talk/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/community-talk/${id}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/community-talk"] });
      toast({ description: "게시글이 수정되었습니다" });
      navigate(`/community-talk/${id}`);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        description: error.message || "게시글 수정에 실패했습니다",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    if (!user) {
      toast({
        variant: "destructive",
        description: "로그인이 필요합니다",
      });
      return;
    }

    let locationData: Record<string, unknown> = {
      state: data.region,
      region: data.region,
      suburb: data.suburb,
      contentIntent: effectiveIntent,
    };

    const zoneId = inferZoneIdFromLocation({
      suburb: data.suburb,
      state: data.region,
    });
    locationData.zone_id = zoneId;
    locationData.zoneId = zoneId;

    if (
      isRestaurantCategory &&
      (data.restaurantAddress || data.restaurantLat || data.restaurantLng)
    ) {
      locationData = {
        ...locationData,
        address: data.restaurantAddress || "",
        lat: data.restaurantLat,
        lng: data.restaurantLng,
      };
    }

    const resolvedMetadata = resolveTopicPostMetadata({
      topicId: data.topicId,
      topicStageId: data.topicStageId,
      contentIntent: effectiveIntent,
    }).metadata;

    const submitData: Record<string, unknown> = {
      title: data.title,
      content: data.content,
      category: data.category,
      region: data.region,
      suburb: data.suburb,
      imageUrls: data.imageUrls || [],
      location: JSON.stringify(locationData),
    };

    if (metadataPersistenceEnabled) {
      submitData.metadata = data.topicId ? resolvedMetadata : null;
    }

    if (isEditMode) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  if (!user) {
    return (
      <>
      <Seo
        title={`${isQuestion ? "생활 질문 작성" : isExperience ? "해결 경험 공유" : "동네 이야기 작성"} — OZK`}
        description={isQuestion ? "호주 생활문제에 대해 현재 상황을 구체적으로 질문하세요." : isExperience ? "직접 해결한 호주 생활문제의 과정과 결과를 공유하세요." : "가벼운 일상과 동네 소식, 이웃과 나누고 싶은 이야기를 작성하세요."}
        path={isQuestion ? "/questions/new" : isExperience ? "/experiences/new" : "/community-talk/new"}
      />
      <SignInRequired
        title={isQuestion ? "생활 질문하기" : isExperience ? "해결 경험 공유하기" : "동네 이야기 작성"}
        description="작성자 확인과 답변 알림을 위해 로그인해주세요. 로그인 후 이 작성 화면에서 바로 계속할 수 있습니다."
        backHref="/community-talk"
        backLabel="동네 톡톡 목록"
      />
      </>
    );
  }

  return (
    <>
    <Seo
      title={`${isQuestion ? "생활 질문 작성" : isExperience ? "해결 경험 공유" : "동네 이야기 작성"} — OZK`}
      description={isQuestion ? "호주 생활문제에 대해 현재 상황과 이미 시도한 내용을 구체적으로 질문하세요." : isExperience ? "직접 해결한 호주 생활문제의 과정과 결과를 다음 사람에게 공유하세요." : "가벼운 일상과 동네 소식, 이웃과 나누고 싶은 이야기를 작성하세요."}
      path={isQuestion ? "/questions/new" : isExperience ? "/experiences/new" : "/community-talk/new"}
    />
    <div className="min-h-screen bg-background py-8">
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold">
            {isEditMode
              ? isQuestion
                ? "생활 질문 수정"
                : isExperience
                  ? "해결 경험 수정"
                  : "동네 이야기 수정"
              : isQuestion
                ? "생활 질문하기"
                : isExperience
                  ? "해결 경험 공유하기"
                  : "동네 이야기 작성"}
          </h1>
          <Button
            variant="outline"
            onClick={() => navigate("/community-talk")}
            data-testid="button-cancel"
          >
            취소
          </Button>
        </div>

        <Card className="p-6">
          <div className="mb-6 rounded-lg border border-primary/20 bg-primary/5 p-4">
            <p className="font-semibold text-foreground">
              {isQuestion
                ? "답변자가 상황을 이해할 수 있게 적어주세요."
                : isExperience
                  ? "다른 사람이 그대로 따라갈 수 있게 적어주세요."
                  : "가벼운 일상, 동네 소식이나 이웃과 나누고 싶은 이야기를 적어주세요."}
            </p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              {isQuestion
                ? "받은 안내, 중요한 날짜, 거주 주와 이미 확인하거나 시도한 내용을 포함하세요. 개인정보와 문서 번호는 제외하세요."
                : isExperience
                  ? "처음 문제, 실제로 밟은 단계, 걸린 시간이나 비용, 최종 결과와 주의점을 포함하세요."
                  : "서로 존중하며 개인정보, 연락처와 특정인을 향한 비방은 제외해주세요."}
            </p>
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isQuestion ? "질문 분야" : isExperience ? "경험 분야" : "이야기 주제"}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-category">
                          <SelectValue placeholder="카테고리 선택" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {communityTalkCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <div className="mb-3">
                  <Label>{isKorean ? "생활 Topic" : "Life Topic"}</Label>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    {isKorean
                      ? metadataPersistenceEnabled
                        ? "선택한 Topic과 Stage가 게시글에 저장되어 같은 상황의 질문과 경험에 우선 연결됩니다."
                        : "Topic 저장 기능은 현재 준비 단계입니다. 선택 내용은 작성 화면에만 유지되며 게시글에 저장된 것으로 표시되지 않습니다."
                      : metadataPersistenceEnabled
                        ? "Selected Topic and Stage metadata will be saved with the post and used to connect related questions and experiences."
                        : "Topic persistence is still being prepared. Your selection stays in this form and is not shown as saved metadata."}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="topicId"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Topic</FormLabel>
                        <Select
                          value={field.value || "none"}
                          onValueChange={(value) => {
                            const topicId = value === "none" ? "" : value;
                            field.onChange(topicId);
                            form.setValue("topicStageId", "");
                            if (!topicId && !intent) {
                              form.setValue("contentIntent", undefined);
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-talk-topic">
                              <SelectValue
                                placeholder={
                                  isKorean ? "Topic 선택" : "Select topic"
                                }
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">
                              {isKorean ? "선택 안 함" : "No topic"}
                            </SelectItem>
                            {OZK_TOPICS.map((topic) => (
                              <SelectItem key={topic.id} value={topic.id}>
                                {getTopicLabel(topic, locale)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <Label>작성 목적</Label>
                    <div className="flex h-10 items-center rounded-md border border-border bg-background px-3 text-sm font-medium">
                      {isQuestion ? "질문" : isExperience ? "해결 경험" : "동네 이야기"}
                    </div>
                  </div>
                </div>

                {selectedTopic && (
                  <div className="mt-4 space-y-3">
                    <FormField
                      control={form.control}
                      name="topicStageId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Stage</FormLabel>
                          <Select
                            value={field.value || "none"}
                            onValueChange={(value) =>
                              field.onChange(value === "none" ? "" : value)
                            }
                          >
                            <FormControl>
                              <SelectTrigger data-testid="select-talk-topic-stage">
                                <SelectValue
                                  placeholder={
                                    isKorean ? "Stage 선택" : "Select stage"
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">
                                {isKorean ? "선택 안 함" : "No stage"}
                              </SelectItem>
                              {topicStages.map((stage) => (
                                <SelectItem key={stage.id} value={stage.id}>
                                  {getTopicStageLabel(stage, locale)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <p className="text-xs leading-5 text-muted-foreground">
                      {isKorean
                        ? stageCommunityContent
                          ? selectedIntent === "experience"
                            ? stageCommunityContent.experiencePrompt
                            : stageCommunityContent.questionPrompt
                          : topicCommunityContent?.intro
                        : getTopicDescription(selectedTopic, locale)}
                    </p>
                  </div>
                )}
              </div>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isQuestion ? "무엇이 궁금한가요?" : isExperience ? "어떤 문제를 해결했나요?" : "제목"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          isQuestion
                            ? "예: 집주인이 수리도, 본드 반환도 해주지 않아요"
                            : isExperience
                              ? "예: 렌트 본드를 돌려받은 과정과 걸린 시간"
                              : "제목을 입력하세요"
                        }
                        {...field}
                        data-testid="input-title"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isQuestion ? "현재 상황과 이미 해본 것" : isExperience ? "해결 과정과 결과" : "내용"}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={
                          isQuestion
                            ? "현재 상황, 받은 안내, 중요한 날짜, 거주 지역, 이미 확인하거나 시도한 내용을 적어주세요. (최소 10자)"
                            : isExperience
                              ? "문제가 무엇이었는지, 어떤 순서로 해결했는지, 시간·비용, 최종 결과와 주의점을 적어주세요. (최소 10자)"
                              : "내용을 입력하세요 (최소 10자)"
                        }
                        rows={10}
                        {...field}
                        data-testid="textarea-content"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="imageUrls"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>사진</FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <MarketImageUploader
                          maxImages={10}
                          onImagesChange={field.onChange}
                          userId={user.uid}
                          initialImages={field.value || []}
                        />
                        <p className="text-xs text-muted-foreground">
                          최대 10장까지 등록할 수 있습니다. 첫 번째 사진이 대표 이미지로 표시됩니다.
                        </p>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="text-sm text-muted-foreground">
                위치: {form.watch("suburb")}, {form.watch("region")}
              </div>

              {isRestaurantCategory && (
                <div className="space-y-4 border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="restaurant-address">식당 주소 (선택)</Label>
                    <AddressAutocomplete
                      value={form.watch("restaurantAddress") || ""}
                      onChange={(address, details) => {
                        form.setValue("restaurantAddress", address);
                        if (details) {
                          form.setValue("restaurantLat", details.lat);
                          form.setValue("restaurantLng", details.lng);
                        }
                      }}
                      placeholder="식당 주소를 입력하세요"
                      label=""
                    />
                    <p className="text-xs text-muted-foreground">
                      주소를 입력하면 지도 위치가 게시글의 위치 정보에 함께 저장됩니다.
                    </p>
                  </div>

                  {form.watch("restaurantLat") &&
                    form.watch("restaurantLng") && (
                      <div className="space-y-2">
                        <Label>지도 미리보기</Label>
                        <BusinessMap
                          lat={form.watch("restaurantLat")!}
                          lng={form.watch("restaurantLng")!}
                          address={form.watch("restaurantAddress") || ""}
                        />
                      </div>
                    )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/community-talk")}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  data-testid="button-submit"
                >
                  {isEditMode
                    ? "수정하기"
                    : isQuestion
                      ? "질문 등록"
                      : isExperience
                        ? "해결 경험 등록"
                        : "작성하기"}
                </Button>
              </div>
            </form>
          </Form>
        </Card>
      </div>
    </div>
    </>
  );
}

function parseLocation(
  value: CommunityTalkEditDTO["location"],
): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}
